/**
 * WeDev — predictive search:
 * - Try /search/suggest Section Rendering (Etzmaleh) when section_id is set
 * - Fall back to /search Section Rendering (wd-predictive-search-results) when suggest is empty
 * - Dropdown + full search use prefix query: q={term}* (trailing wildcard only)
 *
 * Load: layout/theme.liquid after theme.js
 */
(function () {
  'use strict';

  var cachedResponses = new Map();
  var CACHE_MAX = 50;
  var RESULTS_SECTION_ID = 'wd-predictive-search-results';

  function getCachedResponse(url) {
    if (!cachedResponses.has(url)) return undefined;

    var html = cachedResponses.get(url);
    cachedResponses.delete(url);
    cachedResponses.set(url, html);
    return html;
  }

  function setCachedResponse(url, html) {
    if (cachedResponses.has(url)) {
      cachedResponses.delete(url);
    } else if (cachedResponses.size >= CACHE_MAX) {
      var oldestKey = cachedResponses.keys().next().value;
      cachedResponses.delete(oldestKey);
    }

    cachedResponses.set(url, html);
  }

  function cleanSearchTerm(term) {
    return term.replace(/\*/g, '').trim();
  }

  function toPrefixQuery(term) {
    var clean = cleanSearchTerm(term);
    return clean ? clean + '*' : '';
  }

  function getSearchBaseUrl() {
    var searchUrl = theme.routes.search || '/search';

    // Wedev: locale-prefixed /he/search breaks Section Rendering search context on shopify theme dev
    if (location.port === '9292' && searchUrl !== '/search' && /\/search$/.test(searchUrl)) {
      return '/search';
    }

    return searchUrl;
  }

  function getSuggestSearchUrl(searchTerm, limit, sectionId, paramUrlFn) {
    var suggestUrl = theme.routes.predictiveSearch || '/search/suggest';
    var searchObj = {
      q: searchTerm,
      'resources[limit]': limit,
      'resources[limit_scope]': 'each'
    };

    return suggestUrl + '?' + paramUrlFn(searchObj) + '&section_id=' + encodeURIComponent(sectionId);
  }

  function getSearchSectionUrl(searchTerm, paramUrlFn) {
    var searchObj = {
      q: toPrefixQuery(searchTerm),
      type: 'product'
    };

    return getSearchBaseUrl() + '?' + paramUrlFn(searchObj) + '&section_id=' + RESULTS_SECTION_ID;
  }

  function extractSuggestHtml(responseText, sectionId) {
    var parsed = new DOMParser().parseFromString(responseText, 'text/html');
    var resultsEl = parsed.getElementById('PredictiveSearchResults-' + sectionId);

    if (resultsEl && resultsEl.innerHTML.trim()) {
      return resultsEl.innerHTML;
    }

    return '';
  }

  function extractSearchSectionHtml(responseText) {
    var parsed = new DOMParser().parseFromString(responseText, 'text/html');
    var section = parsed.querySelector('#shopify-section-' + RESULTS_SECTION_ID);

    if (!section) {
      section = parsed.querySelector('[id*="wd-predictive-search-results"]');
    }

    if (section && section.innerHTML.trim()) {
      return section.innerHTML;
    }

    return '';
  }

  function fetchSuggestHtml(searchTerm, limit, sectionId, paramUrlFn) {
    var url = getSuggestSearchUrl(searchTerm, limit, sectionId, paramUrlFn);

    if (cachedResponses.has(url)) {
      return Promise.resolve(getCachedResponse(url));
    }

    return fetch(url)
      .then(function (response) {
        if (!response.ok) {
          throw new Error(response.status);
        }
        return response.text();
      })
      .then(function (text) {
        var html = extractSuggestHtml(text, sectionId);
        if (html) {
          setCachedResponse(url, html);
        }
        return html;
      })
      .catch(function () {
        return '';
      });
  }

  function fetchSearchSectionHtml(searchTerm, paramUrlFn) {
    var url = getSearchSectionUrl(searchTerm, paramUrlFn);

    if (cachedResponses.has(url)) {
      return Promise.resolve(getCachedResponse(url));
    }

    return fetch(url)
      .then(function (response) {
        if (!response.ok) {
          throw new Error(response.status);
        }
        return response.text();
      })
      .then(function (text) {
        var html = extractSearchSectionHtml(text);
        if (html) {
          setCachedResponse(url, html);
        }
        return html;
      })
      .catch(function () {
        return '';
      });
  }

  function fetchDropdownHtml(searchTerm, limit, sectionId, paramUrlFn) {
    if (sectionId) {
      return fetchSuggestHtml(searchTerm, limit, sectionId, paramUrlFn).then(function (html) {
        if (html) {
          return html;
        }
        return fetchSearchSectionHtml(searchTerm, paramUrlFn);
      });
    }

    return fetchSearchSectionHtml(searchTerm, paramUrlFn);
  }

  function activateSearchDropdown(predictiveSearch) {
    predictiveSearch.classList.add('is-active');
    document.body.classList.add('predictive-overflow-hidden');
  }

  function renderDropdownHtml(predictiveSearch, html) {
    if (!html) return;
    if (!predictiveSearch.predictiveSearchResults) return;

    predictiveSearch.predictiveSearchResults.innerHTML = html;
    activateSearchDropdown(predictiveSearch);
    predictiveSearch.open();
    hideInitialView(predictiveSearch);
    bindViewMoreButton(predictiveSearch);

    if (typeof AOS !== 'undefined') {
      AOS.refreshHard();
    }
  }

  function patchPredictiveSearchLimit() {
    var PredictiveSearchElement = customElements.get('predictive-search');
    if (!PredictiveSearchElement || !PredictiveSearchElement.prototype.getSearchResults) return;

    PredictiveSearchElement.prototype.getSearchResults = function (searchTerm) {
      var sectionId = this.dataset.sectionId;
      var limit = this.dataset.resultsLimit || 6;

      fetchDropdownHtml(searchTerm, limit, sectionId, this.paramUrl.bind(this))
        .then(function (html) {
          renderDropdownHtml(this, html);
        }.bind(this));
    };
  }

  function getInitialView(predictiveSearch) {
    return predictiveSearch.querySelector('#predictive-search-initial');
  }

  function showInitialView(predictiveSearch) {
    var initial = getInitialView(predictiveSearch);
    var results = predictiveSearch.querySelector('#predictive-search');
    if (!initial) return;

    initial.hidden = false;
    initial.style.display = 'block';
    if (results) {
      results.style.display = 'none';
      results.innerHTML = '';
    }
    activateSearchDropdown(predictiveSearch);
  }

  function hideInitialView(predictiveSearch) {
    var initial = getInitialView(predictiveSearch);
    if (initial) {
      initial.hidden = true;
      initial.style.display = '';
    }
  }

  function hasInitialContent(predictiveSearch) {
    var initial = getInitialView(predictiveSearch);
    return initial && initial.querySelector('.wd-predictive-search__initial-grid');
  }

  function bindViewMoreButton(predictiveSearch) {
    var btn = predictiveSearch.querySelector('.results__search-btn');
    var form = predictiveSearch.querySelector('form');
    if (!btn || !form || btn.dataset.wdBound) return;

    btn.dataset.wdBound = 'true';
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.submit();
      }
    });
  }

  function bindFormSubmitHandler(predictiveSearch) {
    var form = predictiveSearch.querySelector('form');
    if (!form || form.dataset.wdSubmitHandler) return;
    form.dataset.wdSubmitHandler = 'true';

    form.addEventListener('submit', function (e) {
      var input = form.querySelector('input[name="q"]');
      if (!input) return;

      var term = input.value.trim();
      if (!term) {
        e.preventDefault();
        return;
      }

      e.preventDefault();

      var params = new URLSearchParams();
      params.set('q', toPrefixQuery(term));
      params.set('type', 'product');

      var action = (form.getAttribute('action') || '/search').split('?')[0];
      var url = action + '?' + params.toString();

      window.location.assign(url);
    });
  }

  function bindPopularTermClicks(predictiveSearch) {
    predictiveSearch.querySelectorAll('.wd-predictive-search__term').forEach(function (btn) {
      if (btn.dataset.wdBound) return;
      btn.dataset.wdBound = 'true';

      btn.addEventListener('click', function () {
        var term = btn.dataset.searchTerm || btn.textContent.trim();
        if (!term) return;

        var form = predictiveSearch.querySelector('form');
        var params = new URLSearchParams();
        params.set('q', toPrefixQuery(term));
        params.set('type', 'product');

        var action = (form && form.getAttribute('action')) || getSearchBaseUrl();
        var url = action.split('?')[0] + '?' + params.toString();

        window.location.assign(url);
      });
    });
  }

  function enhancePredictiveSearch(predictiveSearch) {
    var input = predictiveSearch.querySelector('input[type="search"]');
    if (!input || input.dataset.wdEnhanced) return;
    input.dataset.wdEnhanced = 'true';

    bindPopularTermClicks(predictiveSearch);
    bindFormSubmitHandler(predictiveSearch);

    if (input.value.indexOf('*') !== -1) {
      input.value = cleanSearchTerm(input.value);
    }

    input.addEventListener('focus', function () {
      if (!input.value.trim() && hasInitialContent(predictiveSearch)) {
        showInitialView(predictiveSearch);
        return;
      }

      if (input.value.trim()) {
        activateSearchDropdown(predictiveSearch);
        if (typeof predictiveSearch.getSearchResults === 'function') {
          predictiveSearch.getSearchResults(input.value.trim());
        }
      }
    });

    input.addEventListener('input', function () {
      var results = predictiveSearch.querySelector('#predictive-search');
      if (!input.value.trim() && hasInitialContent(predictiveSearch)) {
        showInitialView(predictiveSearch);
      } else {
        hideInitialView(predictiveSearch);
        if (results) {
          results.style.display = 'block';
        }
      }
    });

    document.addEventListener('predictive-search:open', function (e) {
      if (e.detail.context !== predictiveSearch.getAttribute('data-context')) return;

      setTimeout(function () {
        if (!input.value.trim() && hasInitialContent(predictiveSearch)) {
          showInitialView(predictiveSearch);
        }
      }, 120);
    });
  }

  function init() {
    patchPredictiveSearchLimit();

    document.querySelectorAll('predictive-search').forEach(function (el) {
      enhancePredictiveSearch(el);
    });

    document.addEventListener('predictive-search:close-all', function () {
      document.querySelectorAll('predictive-search').forEach(function (el) {
        var initial = getInitialView(el);
        if (initial) initial.hidden = true;
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
